import { GLOBALS } from "../constants/constants";
import {
  convertRgbToRgba,
  darken,
  getContrastColor,
  getContrastColorWithPriority,
  lightenRGBColor,
} from "../utility/Colors";
import { generateUID } from "../utility/utility";

class StyleManagerInternal {
  constructor() {
    this.instanceStyle = [];
    this.classes = {};
    this.styleEl = {};
    this.styleInserted = false;
    this.counter = 0;
    this.cachedUserStyles = [];
    this.stylesArray = [];
  }

  getClassname(name) {
    this.classes[name] = this.classes[name] >= 0 ? this.classes[name] + 1 : 0;
    return name + "-" + this.classes[name];
  }
  insertRule(css) {
    this.styleEl.sheet?.insertRule(css, this.styleEl.sheet.cssRules.length);
    this.stylesArray.push(css);
  }
  deleteRule(index) {
    this.styleEl.sheet?.deleteRule(index);
  }
  setProperty(property, value, index = 0) {
    if (
      this.styleEl.sheet.cssRules.length &&
      this.styleEl.sheet.cssRules[index]
    ) {
      this.styleEl.sheet.cssRules[index].style.setProperty(property, value);
    }
  }
  setElementAttribute(attribName, el, value, sectionId) {
    let className = Array.from(el.classList).find((i) => i.includes("---"));
    if (className) {
      const rules = this.styleEl.sheet.cssRules;
      for (let i of rules) {
        if (i.selectorText?.includes(className)) {
          i.style.setProperty(attribName, value);
          const cachedStyle = this.cachedUserStyles.find(
            (ustyle) => ustyle.selector === i.selectorText
          );
          if (cachedStyle) {
            cachedStyle[attribName] = value;
          }
        }
      }
      console.log(this.cachedUserStyles);
    } else {
      className = `---${generateUID()}`;
      const selector = `[data-section-id="${sectionId}"] .${className}`;
      const ruleObj = { selector: selector, [attribName]: value };
      Object.defineProperty(ruleObj, "rule", {
        get: function () {
          const propStr = Object.keys(this)
            .map((i) => {
              if (i != "selector") {
                return `${i}:${this[i]};`;
              }
            })
            .filter(Boolean);
          return `${this.selector}{${propStr.join("\n")}}`;
        },
      });
      this.cachedUserStyles.push(ruleObj);
      this.registerRules([{ className: className, rule: ruleObj.rule }]);
    }
    return className;
  }
  getProperty(property, index = 0) {
    if (
      this.styleEl.sheet.cssRules.length &&
      this.styleEl.sheet.cssRules[index]
    ) {
      return this.styleEl.sheet.cssRules[index].style.getPropertyValue(
        property
      );
    }
  }

  generateProperties(styles, style) {
    let properties = "";
    Object.keys(styles[style]).forEach((props) => {
      properties =
        properties +
        `${StyleManager.kebabize(props)} : ${styles[style][props]};`;
    });
    properties = `{${properties}}`;
    return { className: style, rule: `${style} ${properties}` };
  }
  modifyRule(index, rule) {
    this.styleEl.sheet?.insertRule(rule, index);
    this.styleEl.sheet?.deleteRule(index + 1);
    if (this.stylesArray[index]) {
      this.stylesArray[index] = rule;
    }
  }

  generateStyle(styles) {
    const rules = [];
    Object.keys(styles).forEach((style) => {
      if (style.indexOf("@") > -1) {
        const mediaStyles = styles[style];
        const mRules = [];
        Object.keys(mediaStyles).forEach((mStyle) => {
          mRules.push(this.generateProperties(mediaStyles, mStyle));
        });
        const className = mRules.reduce((a, b) => a + b.className, style);

        rules.push({
          className: className,
          rule: `${style} { ${mRules.map((i) => i.rule).join(" ")} }`,
        });
      } else {
        rules.push(this.generateProperties(styles, style));
      }
    });
    this.registerRules(rules);
  }
  generateStylesWithParentId(styleObj, id) {
    const mapIdToStyle = (styles, id) => {
      const stylesWithId = {};
      Object.keys(styles).forEach((i) => {
        stylesWithId[`[data-section-id="${id}"] ${i}`] = styles[i];
      });
      return stylesWithId;
    };
    this.generateStyle(mapIdToStyle(styleObj, id));
  }
  generateGlobalStyle(Globals) {
    const primaryColor =
      Globals[GLOBALS.COLOR3] || Globals[GLOBALS.PRIMARY_COLOR];
    this.generateStyle({
      ":root": {
        "--body-color": Globals[GLOBALS.BODY_COLOR],
        "--primary700": primaryColor,
        "--primary800": darken(primaryColor, 20),
        "--primary200": lightenRGBColor(primaryColor, 80),
        "--primary100": lightenRGBColor(primaryColor, 90),
        "--primary50": lightenRGBColor(primaryColor, 95),
        "--primary-hover": lightenRGBColor(primaryColor, 5),
        "--secondary-hover": lightenRGBColor(
          Globals[GLOBALS.SECONDARY_BUTTON_BG],
          5
        ),
        "--secondary-button-bg": Globals[GLOBALS.SECONDARY_BUTTON_BG],
        "--primary-button-text": Globals[GLOBALS.PRIMARY_BUTTON_TEXT],
        "--secondary-button-text": Globals[GLOBALS.SECONDARY_BUTTON_TEXT],
        "--primary-button-rd": Globals[GLOBALS.PRIMARY_BUTTON_RD],
        "--secondary-button-rd": Globals[GLOBALS.SECONDARY_BUTTON_RD],
        "--white": "#ffffff",
        "--gray25": "#FCFCFD",
        "--gray50": "#F9FAFB",
        "--gray100": "#F2F4F7",
        "--gray200": "#EAECF0",
        "--gray300": "#D0D5DD",
        "--gray400": "#98A2B3",
        "--gray500": "#667085",
        "--gray600": "#475467",
        "--gray700": "#344054",
        "--gray800": "#1D2939",
        "--gray900": "#101828",
        "--color1": Globals["color1"],
        "--color2": Globals["color2"],
        "--color3": Globals["color3"],
        "--color4": Globals["color4"],
        "--color5": Globals["color5"],
        "--color1safe": getContrastColorWithPriority(
          Globals["color1"],
          Globals["color3"],
          Globals["color5"]
        ),
        "--color2safe": getContrastColorWithPriority(
          Globals["color2"],
          Globals["color3"],
          Globals["color5"]
        ),
        "--color4safe": getContrastColorWithPriority(
          Globals["color4"],
          Globals["color3"],
          Globals["color5"]
        ),
        "--color3inverse": getContrastColor(
          Globals["color3"],
          Globals["color1"],
          Globals["color5"]
        ),
        "--color1inverse": getContrastColor(
          Globals["color1"],
          Globals["color1"],
          Globals["color5"]
        ),
        "--color2inverse": getContrastColor(
          Globals["color2"],
          Globals["color1"],
          Globals["color5"]
        ),
        "--color4inverse": getContrastColor(
          Globals["color4"],
          Globals["color1"],
          Globals["color5"]
        ),
        "--color5inverse": getContrastColor(
          Globals["color5"],
          Globals["color1"],
          Globals["color5"]
        ),
        "--color1alpha30": convertRgbToRgba(Globals["color1"], 0.3),
        "--color2alpha30": convertRgbToRgba(Globals["color2"], 0.3),
        "--color3alpha30": convertRgbToRgba(Globals["color3"], 0.3),
        "--color4alpha30": convertRgbToRgba(Globals["color4"], 0.3),
        "--color5alpha30": convertRgbToRgba(Globals["color5"], 0.3),
        "--color5alpha70": lightenRGBColor(Globals["color5"], 35),
        "--color1alpha70": lightenRGBColor(Globals["color1"], 35),
        "--headerTitleFont": Globals[GLOBALS.HEADER_TITLE_FONT] || "Inter",
        "--header-line-height": Globals[GLOBALS.HEADER_LINE_HEIGHT],
        "--header-font-weight": Globals[GLOBALS.HEADER_FONT_WEIGHT],
        "--bodyFont": Globals[GLOBALS.Body_FONT] || "Inter",
        "--body-line-height": Globals[GLOBALS.BODY_LINE_HEIGHT],
        "--body-font-weight": Globals[GLOBALS.BODY_FONT_WEIGHT],
        "--font-size-h1": Globals[GLOBALS.FONT_SIZE_H1],
        "--font-size-h2": Globals[GLOBALS.FONT_SIZE_H2],
        "--font-size-h3": Globals[GLOBALS.FONT_SIZE_H3],
        "--font-size-h4": Globals[GLOBALS.FONT_SIZE_H4],
        "--font-size-p1": Globals[GLOBALS.FONT_SIZE_P1],
        "--font-size-p2": Globals[GLOBALS.FONT_SIZE_P2],
        "--font-size-p3": Globals[GLOBALS.FONT_SIZE_P3],
        "--font-size-p4": Globals[GLOBALS.FONT_SIZE_P4],
      },
    });
  }
  registerRules(rules) {
    rules.forEach((i) => {
      const cachedIndex = this.getChachedIndex(i.className);
      if (cachedIndex >= 0) {
        this.modifyRule(cachedIndex, i.rule);
      } else {
        this.cacheClassName(i.className);
        this.insertRule(i.rule);
      }
    });
  }
  cacheClassName(className) {
    this.classes[className] = this.counter++;
  }
  getChachedIndex(className) {
    return this.classes[className];
  }
  setInstance(instance, userStyles) {
    if (!this.instanceEl) {
      this.instanceEl = instance;
      this.styleEl = document.createElement("style");
      this.instanceEl.head.appendChild(this.styleEl);
      this.styleEl.type = "text/css";
      if (userStyles && userStyles.length) {
        this.setCachedUserStyles(userStyles);
      }
    }
  }
  getInstance() {
    return this.instanceEl;
  }
  getStyles() {
    return this.stylesArray.join("");
  }
  getCachedUserStyles() {
    return this.cachedUserStyles;
  }
  setCachedUserStyles(styles) {
    function ABC(style) {
      Object.defineProperty(style, "rule", {
        get: function () {
          const propStr = Object.keys(this)
            .map((i) => {
              if (i != "selector") {
                return `${i}:${this[i]};`;
              }
            })
            .filter(Boolean);
          return `${this.selector}{${propStr.join("\n")}}`;
        },
      });
      return style;
    }
    const stylesToRegister = styles.map((i) => {
      this.cachedUserStyles.push(ABC(i));
      return { className: i.selector, rule: i.rule };
    });
    this.registerRules(stylesToRegister);
  }
  kebabize(str) {
    return str.replace(
      /[A-Z]+(?![a-z])|[A-Z]/g,
      ($, ofs) => (ofs ? "-" : "") + $.toLowerCase()
    );
  }
  toCamelCase(str) {
    return str.replace(/-\w/g, (m) => m[1].toUpperCase());
  }
}
export const StyleManager = new StyleManagerInternal();
